/**
 * Meta (Facebook) Pixel.
 *
 * The base snippet is injected from here rather than from public/index.html so
 * it only loads when REACT_APP_META_PIXEL_ID is configured — a missing ID means
 * no pixel at all instead of a broken one reporting to nowhere.
 *
 * Every event carries an eventID. That is what lets a server-side Conversions
 * API call for the same action be de-duplicated against the browser event, so
 * adding CAPI later needs no changes on this side.
 */

import {
  captureAttribution,
  claimAdVisitOncePerSession,
  getAttributionParams,
  isMetaTraffic,
} from './attribution';

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

/**
 * A pixel ID is a public identifier (it ships in the page source either way),
 * so it is safe to default here — that keeps tracking working without adding an
 * env var to every deploy target. Set REACT_APP_META_PIXEL_ID to override, e.g.
 * to point staging at a separate test pixel.
 */
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1560016759119158';
const CURRENCY = 'NGN';

let initialized = false;

const newEventId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const injectPixelScript = () => {
  if (window.fbq) return;

  const fbq: any = function () {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
  };

  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
};

/**
 * Loads the pixel and captures the visitor's traffic source. Call once, as
 * early as possible — before the first PageView.
 */
export const initMetaPixel = () => {
  if (initialized || typeof window === 'undefined') return;

  if (!PIXEL_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[metaPixel] REACT_APP_META_PIXEL_ID is not set — no events will be sent to Meta.'
      );
    }
    return;
  }

  injectPixelScript();
  window.fbq('init', PIXEL_ID);
  initialized = true;

  captureAttribution();
};

export const isPixelReady = () => Boolean(initialized && window.fbq);

const track = (
  kind: 'track' | 'trackCustom',
  event: string,
  params?: Record<string, any>,
  eventId?: string
) => {
  if (!isPixelReady()) return;
  window.fbq(kind, event, params || {}, { eventID: eventId || newEventId() });
};

/* ── Page views ──────────────────────────────────────────────────────────── */

/**
 * Standard PageView — this is what Meta's own ad reporting and optimisation
 * consume. Fire on every route change; the SPA never reloads the document, so
 * the pixel cannot detect navigation on its own.
 */
export const trackPageView = () => {
  track('track', 'PageView', getAttributionParams());
};

/**
 * Custom "PageReached" event carrying the page name plus campaign params.
 * Standard PageView can't be broken down by page in Events Manager; this can,
 * which is what answers "which pages did my ad traffic actually reach?".
 */
export const trackPageReached = (pageName: string, pagePath: string) => {
  track('trackCustom', 'PageReached', {
    page_name: pageName,
    page_path: pagePath,
    ...getAttributionParams(),
  });
};

/**
 * Fired once per session when the visitor arrived from a Meta ad. Gives a clean
 * denominator for "visits from Meta ads" without mixing in internal navigation.
 */
export const trackMetaAdVisit = (pageName: string, pagePath: string) => {
  if (!isMetaTraffic()) return;
  if (!claimAdVisitOncePerSession()) return;

  track('trackCustom', 'MetaAdVisit', {
    entry_page_name: pageName,
    entry_page_path: pagePath,
    ...getAttributionParams(),
  });
};

/* ── Funnel events ───────────────────────────────────────────────────────── */

export const trackViewContent = (item: {
  id: string;
  title: string;
  price: number;
}) => {
  track('track', 'ViewContent', {
    content_name: item.title,
    content_ids: [item.id],
    content_type: 'product',
    value: item.price,
    currency: CURRENCY,
    ...getAttributionParams(),
  });
};

export const trackAddToCart = (item: {
  id: string;
  title: string;
  price: number;
  quantity: number;
}) => {
  track('track', 'AddToCart', {
    content_name: item.title,
    content_ids: [item.id],
    content_type: 'product',
    contents: [{ id: item.id, quantity: item.quantity, item_price: item.price }],
    value: item.price * item.quantity,
    currency: CURRENCY,
    ...getAttributionParams(),
  });
};

export const trackInitiateCheckout = (total: number, cartItems: any[]) => {
  track('track', 'InitiateCheckout', {
    value: total,
    currency: CURRENCY,
    content_type: 'product',
    content_ids: cartItems.map((i) => String(i.id)),
    contents: cartItems.map((i) => ({
      id: String(i.id),
      quantity: i.quantity,
      item_price: i.price,
    })),
    num_items: cartItems.reduce((sum, i) => sum + (i.quantity || 0), 0),
    ...getAttributionParams(),
  });
};

/**
 * Fired when the coming-soon teaser is opened. Without it, ad traffic that hits
 * the teaser looks identical to a bounce — landed and went no further — when in
 * fact the visitor engaged and there was simply nothing to buy yet.
 */
export const trackComingSoon = () => {
  track('trackCustom', 'ComingSoonViewed', {
    content_name: 'Coming Soon Teaser',
    ...getAttributionParams(),
  });
};

/** Contact via the WhatsApp floater. */
export const trackContact = (pageName: string) => {
  track('track', 'Contact', {
    content_name: 'WhatsApp Floater',
    channel: 'whatsapp',
    page_name: pageName,
    ...getAttributionParams(),
  });
};

/* ── Purchase ────────────────────────────────────────────────────────────── */

const PURCHASE_KEY_PREFIX = 'immersia_purchase_sent_';

/**
 * Guards against double-counting when the ticket page is refreshed or
 * navigated back to. Meta also de-duplicates on eventID, but only for ~48h and
 * only per-pixel, so we keep a local guard too.
 */
const alreadySentPurchase = (reference: string) => {
  try {
    const key = PURCHASE_KEY_PREFIX + reference;
    if (window.localStorage.getItem(key)) return true;
    window.localStorage.setItem(key, String(Date.now()));
    return false;
  } catch {
    return false;
  }
};

/**
 * Successful payment. Fired from the ticket page, which is only reachable after
 * the backend has verified the Paystack reference — so it represents money
 * actually received, not just a completed checkout form.
 *
 * `reference` doubles as the eventID: pass the same value from a Conversions
 * API call and Meta will treat the two as one conversion.
 */
export const trackPurchase = (
  total: number,
  cartItems: any[],
  reference?: string
) => {
  if (!isPixelReady()) return;
  if (reference && alreadySentPurchase(reference)) return;

  track(
    'track',
    'Purchase',
    {
      value: total,
      currency: CURRENCY,
      content_type: 'product',
      content_ids: cartItems.map((i) => String(i.id)),
      contents: cartItems.map((i) => ({
        id: String(i.id),
        quantity: i.quantity,
        item_price: i.price,
      })),
      num_items: cartItems.reduce((sum, i) => sum + (i.quantity || 0), 0),
      transaction_id: reference,
      ...getAttributionParams(),
    },
    reference
  );
};
